export type SrcsetCandidate = {
  url: string;
  width?: number;
  density?: number;
};

/** Parse a comma-separated `srcset` attribute into URL + descriptor pairs. */
export function parseSrcset(srcset: string): SrcsetCandidate[] {
  const trimmed = srcset.trim();
  if (!trimmed) return [];

  const out: SrcsetCandidate[] = [];
  for (const piece of trimmed.split(',')) {
    const parts = piece.trim().split(/\s+/).filter(Boolean);
    const url = parts[0];
    if (!url) continue;

    const descriptor = parts[1];
    if (!descriptor) {
      out.push({ url });
      continue;
    }
    if (descriptor.endsWith('w')) {
      const width = Number.parseInt(descriptor.slice(0, -1), 10);
      out.push({ url, width: Number.isFinite(width) ? width : undefined });
      continue;
    }
    if (descriptor.endsWith('x')) {
      const density = Number.parseFloat(descriptor.slice(0, -1));
      out.push({
        url,
        density: Number.isFinite(density) ? density : undefined,
      });
      continue;
    }
    out.push({ url });
  }
  return out;
}

/** Pick the `src` fallback the UA should use for a given `srcset`. */
export function resolveImageFallbackSrc(src: string, srcset: string): string {
  const explicit = src.trim();
  const candidates = parseSrcset(srcset);
  if (!candidates.length) return explicit;

  if (explicit && candidates.some((c) => c.url === explicit)) {
    return explicit;
  }

  const widthCandidates = candidates.filter(
    (c): c is SrcsetCandidate & { width: number } =>
      typeof c.width === 'number' && c.width > 0
  );
  if (widthCandidates.length) {
    return widthCandidates.reduce((best, c) =>
      c.width >= best.width ? c : best
    ).url;
  }

  const densityCandidates = candidates.filter(
    (c): c is SrcsetCandidate & { density: number } =>
      typeof c.density === 'number' && c.density > 0
  );
  if (densityCandidates.length) {
    return densityCandidates.reduce((best, c) =>
      c.density >= best.density ? c : best
    ).url;
  }

  return candidates[0]?.url ?? explicit;
}
